"use client";

import * as React from "react";
import { ChevronRight, Search } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { ProductsSearch } from "@/components/shop/ProductsSearch";
import { AddToCartButton, EcommerceProductsProvider, useEcommerceProducts } from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
import { Link } from "@/components/common/Link";
import { WP_Query } from "@/integrations/wordpress/wp_query";
const filterCategories = [{
  id: "all",
  label: "ALL"
}, {
  id: "panels",
  label: "PANELS"
}, {
  id: "roofing",
  label: "ROOFING"
}, {
  id: "steel",
  label: "STEEL"
}, {
  id: "facade",
  label: "FACADE"
}, {
  id: "interior",
  label: "INTERIOR"
}, {
  id: "accessories",
  label: "ACCESSORIES"
}];
function ProductGridConsumer() {
  const {
    products,
    loading,
    error,
    refetch,
    currentPage,
    totalPages,
    hasNext,
    hasPrev,
    nextPage,
    prevPage,
    setPage
  } = useEcommerceProducts();
  if (loading) {
    return <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {[...Array(6)].map((_, i) => <div key={i} data-index={i} className="aspect-[4/5] border border-border bg-card animate-pulse" />)}
      </div>;
  }
  if (error) {
    return <div className="border border-border bg-card p-8 text-center">
        <p className="font-mono text-sm text-muted-foreground">
          // ERROR: FAILED TO LOAD MATERIALS
        </p>
        <Button onClick={refetch} variant="outline" className="mt-4 font-mono uppercase tracking-wider">
          Retry
        </Button>
      </div>;
  }
  if (products.length === 0) {
    return <div className="border border-dashed border-border bg-card/50 p-12 text-center">
        <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground">
          // NO MATERIALS IN INVENTORY
        </p>
      </div>;
  }
  const getPaginationNumbers = () => {
    const pages: (number | "ellipsis")[] = [];
    if (totalPages <= 6) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1, 2, 3, 4);
      pages.push("ellipsis");
      pages.push(totalPages);
    }
    return pages;
  };
  return <>
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {products.map((product, i) => {
        const stockBadge = i % 5 === 0 ? {
          label: "LIMITED",
          color: "bg-primary text-primary-foreground"
        } : {
          label: "IN STOCK",
          color: "bg-secondary text-secondary-foreground"
        };
        const sku = `SS-${String(product.id).padStart(4, "0")}`;
        return <article data-index={i} key={product.id} className="group relative flex flex-col border border-border bg-card transition-all duration-300 hover:border-primary">
              {}
              <Link to={product.link} className="relative block overflow-hidden bg-background aspect-[4/5]">
                {product.featured_image_url ? <img src={product.featured_image_url} alt={product.title} loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.04]" /> : <div className="flex h-full w-full items-center justify-center bg-muted">
                    <span className="font-mono text-xs uppercase tracking-widest text-muted-foreground">
                      // NO IMAGE
                    </span>
                  </div>}

                {}
                <div className="absolute top-3 left-3 border border-border bg-background/90 px-2 py-1 backdrop-blur-sm">
                  <span className="font-mono text-[10px] uppercase tracking-wider text-foreground">
                    REF: {sku}
                  </span>
                </div>

                {}
                <Badge className={`absolute top-3 right-3 ${stockBadge.color} font-mono text-[10px] uppercase tracking-wider rounded-none`}>
                  {stockBadge.label}
                </Badge>

                {}
                <div className="absolute bottom-3 left-3 font-mono text-[10px] uppercase tracking-widest text-foreground/40">
                  SWISS SOLUTIONS
                </div>
              </Link>

              {}
              <div className="flex flex-1 flex-col gap-3 p-5">
                <Link to={product.link}>
                  <h3 className="font-default text-lg font-semibold leading-tight text-foreground transition-colors group-hover:text-primary">
                    {product.title}
                  </h3>
                </Link>

                {product.short_description ? <div className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground line-clamp-2 [&_p]:m-0" dangerouslySetInnerHTML={{
              __html: product.short_description
            }} /> : <p className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground">
                    // PREMIUM CONSTRUCTION MATERIAL
                  </p>}

                <div className="mt-auto flex items-center justify-between border-t border-border pt-4">
                  <div className="flex flex-col">
                    <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                      PRICE
                    </span>
                    <span className="font-serif text-2xl text-primary">
                      ON REQUEST
                    </span>
                  </div>
                  <AddToCartButton productId={product.id} variant="outline" size="sm" text="INQUIRE" buttonClassName="bg-primary text-primary-foreground font-mono text-[11px] uppercase tracking-wider border-primary hover:bg-primary/90" showQuantity={false} />
                </div>
              </div>
            </article>;
      })}
      </div>

      {}
      {totalPages > 1 && <div className="mt-16 flex flex-wrap items-center justify-center gap-2 border-t border-border pt-8">
          <Button onClick={prevPage} disabled={!hasPrev} variant="ghost" className="font-mono text-xs uppercase tracking-widest text-foreground disabled:opacity-30">
            ← PREV
          </Button>

          <div className="flex items-center gap-2">
            {getPaginationNumbers().map((page, i) => <React.Fragment key={`${page}-${i}`}>
                {i > 0 && <span className="font-mono text-xs text-muted-foreground">--</span>}
                {page === "ellipsis" ? <span className="font-mono text-sm text-muted-foreground px-2">
                    ...
                  </span> : <Button onClick={() => setPage(page)} variant="ghost" size="sm" className={`font-mono text-sm tracking-wider min-w-[36px] ${currentPage === page ? "bg-primary text-primary-foreground hover:bg-primary/90" : "text-foreground hover:text-primary"}`}>
                    {String(page).padStart(2, "0")}
                  </Button>}
              </React.Fragment>)}
          </div>

          <Button onClick={nextPage} disabled={!hasNext} variant="ghost" className="font-mono text-xs uppercase tracking-widest text-foreground disabled:opacity-30">
            NEXT →
          </Button>
        </div>}
    </>;
}
export default function CertifiedDocumentGallery() {
  const [searchValue, setSearchValue] = React.useState("");
  const [activeFilter, setActiveFilter] = React.useState("all");
  const wp_query = React.useMemo(() => {
    const params: Record<string, any> = {
      per_page: 9
    };
    const term = searchValue.trim();
    if (term) params.s = term;
    return new WP_Query(params);
  }, [searchValue]);
  return <section data-section-id="3928" id="certified-document-gallery" className="bg-background text-foreground relative overflow-hidden py-20 md:py-28">
      {}
      <div className="absolute inset-0 opacity-[0.03] pointer-events-none" style={{
      backgroundImage: "linear-gradient(var(--foreground) 1px, transparent 1px), linear-gradient(90deg, var(--foreground) 1px, transparent 1px)",
      backgroundSize: "60px 60px"
    }} aria-hidden="true" />

      <div className="container mx-auto px-4 md:px-8 max-w-7xl relative">
        {}
        <div className="mb-8 flex items-center gap-3">
          <div className="h-px w-12 bg-primary" />
          <span className="font-mono text-xs uppercase tracking-[0.3em] text-primary">
            // 02 — PRODUCT CATALOG
          </span>
        </div>

        {}
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-2 lg:gap-16 mb-12 pb-10 border-b border-border">
          {}
          <div className="flex flex-col gap-4">
            <h2 className="font-default text-5xl md:text-6xl lg:text-7xl uppercase tracking-tight leading-[0.95] text-foreground">
              SWISS QUALITY.
              <br />
              <span className="text-primary">BALKAN STRENGTH.</span>
            </h2>
            <div className="flex items-center gap-2 mt-2">
              <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                BUJANOVAC // SERBIA // EUROPE
              </span>
            </div>
          </div>

          {}
          <div className="flex flex-col gap-6 lg:pt-4">
            <p className="font-default text-base md:text-lg text-muted-foreground leading-relaxed">
              Swiss Solutions provides premium construction materials sourced directly from certified manufacturers. From sandwich panels to complete steel building packages, we ensure industrial-grade quality for every project.
            </p>

            {}
            <div className="relative border-b-2 border-border focus-within:border-primary transition-colors">
              <div className="flex items-center gap-3 pb-2">
                <Search className="h-4 w-4 text-muted-foreground shrink-0" />
                <ProductsSearch value={searchValue} onChange={setSearchValue} placeholder="SEARCH MATERIALS, PANELS, STEEL..." className="flex-1 [&_input]:bg-transparent [&_input]:border-0 [&_input]:px-0 [&_input]:font-mono [&_input]:text-xs [&_input]:uppercase [&_input]:tracking-widest [&_input]:text-foreground [&_input]:placeholder:text-muted-foreground [&_input]:focus-visible:ring-0 [&_input]:focus-visible:border-0 [&_input]:shadow-none [&_input]:h-9" />
              </div>
            </div>
          </div>
        </div>

        {}
        <div className="mb-10 overflow-x-auto">
          <ToggleGroup type="single" value={activeFilter} onValueChange={value => value && setActiveFilter(value)} className="flex flex-wrap gap-2 w-full justify-start">
            {filterCategories.map((cat, i) => <ToggleGroupItem key={cat.id} data-index={i} value={cat.id} className="font-mono text-[11px] uppercase tracking-widest px-4 py-2 h-auto border border-border bg-transparent text-foreground hover:bg-accent hover:text-accent-foreground data-[state=on]:bg-primary data-[state=on]:text-primary-foreground data-[state=on]:border-primary shrink-0">
                {cat.label}
              </ToggleGroupItem>)}
          </ToggleGroup>
        </div>

        {}
        <div className="mb-6 flex items-center justify-between">
          <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
            // INVENTORY DISPLAY — GRID VIEW
          </span>
          <div className="flex items-center gap-2">
            <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground hidden sm:inline">
              STATUS:
            </span>
            <span className="font-mono text-[10px] uppercase tracking-widest text-primary flex items-center gap-1.5">
              <span className="inline-block h-1.5 w-1.5 bg-primary" />
              AVAILABLE
            </span>
          </div>
        </div>

        {}
        <EcommerceProductsProvider wp_query={wp_query}>
          <ProductGridConsumer />
        </EcommerceProductsProvider>

        {}
        <div className="mt-16 flex flex-wrap items-center justify-between gap-4 border-t border-border pt-6">
          <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
            // END OF CATALOG
          </span>
          <Link to="/ContactPage" className="group inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-primary hover:text-primary/80 transition-colors">
            REQUEST A QUOTE
            <ChevronRight className="h-3 w-3 transition-transform group-hover:translate-x-1" />
          </Link>
        </div>
      </div>
    </section>;
}